import { useCallback, useEffect, useState } from "react";
import { Briefcase, History, ShieldCheck, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { PageHeader, EmptyState, Field } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, fmtDateTime, isAdminRole } from "@/lib/api";
import { toast } from "sonner";

const PADRAO = { leitura: ["fiscal", "folha", "ponto", "relatorios"], operar: [] };

function Termo({ texto, value, onChange }) {
  return (
    <label className="flex gap-2 rounded-lg border bg-muted/40 p-3 text-xs">
      <input type="checkbox" className="mt-0.5" checked={value} onChange={(e) => onChange(e.target.checked)} data-testid="contador-termo" />
      <span>{texto}</span>
    </label>
  );
}

function Permissoes({ areas, operaveis, value, onChange }) {
  const tem = (k, a) => value[k].includes(a);
  const alterna = (k, a, on) => {
    const prox = { ...value, [k]: on ? [...new Set([...value[k], a])] : value[k].filter((x) => x !== a) };
    if (k === "leitura" && !on) prox.operar = prox.operar.filter((x) => x !== a);
    onChange(prox);
  };
  return (
    <div className="divide-y rounded-lg border">
      {Object.entries(areas).map(([a, rotulo]) => (
        <div key={a} className="flex items-center gap-3 px-3 py-2 text-sm">
          <span className="flex-1">{rotulo}</span>
          <label className="flex items-center gap-1 text-xs">Consultar<Switch checked={tem("leitura", a)} onCheckedChange={(v) => alterna("leitura", a, v)} /></label>
          {operaveis.includes(a) && (
            <label className="flex items-center gap-1 text-xs">Operar<Switch checked={tem("operar", a)} disabled={!tem("leitura", a)} onCheckedChange={(v) => alterna("operar", a, v)} /></label>
          )}
        </div>
      ))}
    </div>
  );
}

export default function MeuContador() {
  const { session } = useAuth();
  const [d, setD] = useState(null);
  const [novo, setNovo] = useState(null);
  const [edit, setEdit] = useState(null);
  const [hist, setHist] = useState(null);
  const load = useCallback(() => api.get("/contabil/cliente").then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => { load(); }, [load]);
  if (!isAdminRole(session.user)) return <EmptyState icon={Briefcase} title="Apenas o administrador" text="Só o dono ou administrador libera o acesso do contador." />;

  const liberar = async () => {
    try {
      await api.post("/contabil/cliente/liberar", { codigo_escritorio: novo.codigo, ...novo.perm, aceite_termo: novo.aceite });
      toast.success("Escritório liberado. O contador já pode entrar.");
      setNovo(null);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const salvar = async () => {
    try {
      await api.put(`/contabil/cliente/${edit.id}`, { ...edit.perm, aceite_termo: !!edit.aceite });
      toast.success(edit.status === "pendente" ? "Pedido aprovado" : "Permissões salvas");
      setEdit(null);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const revogar = async (v) => {
    if (!window.confirm(`Encerrar o acesso de ${v.escritorio?.nome}? O contador sai do sistema na hora.`)) return;
    try { await api.delete(`/contabil/cliente/${v.id}`); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const verHistorico = () => api.get("/contabil/cliente/historico").then((r) => setHist(r.data)).catch((e) => toast.error(errMsg(e)));

  return (
    <div className="space-y-6" data-testid="meu-contador-page">
      <PageHeader eyebrow="Sistema" title="Meu contador" subtitle="Libere o escritório contábil e escolha o que ele pode ver e fazer"
        actions={<>
          <Button variant="outline" onClick={verHistorico}><History className="mr-2 h-4 w-4" />O que o contador fez</Button>
          <Button onClick={() => setNovo({ codigo: "", perm: PADRAO, aceite: false })} data-testid="contador-liberar-btn"><ShieldCheck className="mr-2 h-4 w-4" />Liberar escritório</Button>
        </>} />
      {!d ? null : !d.vinculos.length ? (
        <EmptyState icon={Briefcase} title="Nenhum escritório liberado" text="Peça ao seu contador o código do escritório (aparece no painel dele) e clique em Liberar escritório." />
      ) : (
        <ul className="space-y-3">
          {d.vinculos.map((v) => (
            <li key={v.id} className="card-surface flex flex-wrap items-center gap-3 p-4">
              <div className="flex-1">
                <p className="font-semibold">{v.escritorio?.nome} <span className="text-xs text-muted-foreground">· código {v.escritorio?.codigo}</span></p>
                <p className="text-xs text-muted-foreground">{v.escritorio?.email}{v.ultimo_acesso ? ` · último acesso ${fmtDateTime(v.ultimo_acesso)}` : ""}</p>
                {v.status === "pendente"
                  ? <p className="mt-1 text-sm text-amber-600">Pediu acesso ao seu sistema. Aprove escolhendo as permissões, ou recuse.</p>
                  : <p className="mt-1 text-sm">Consulta: {v.leitura.map((a) => d.areas[a]).join(", ")}{v.operar.length ? ` · Opera: ${v.operar.map((a) => d.areas[a]).join(", ")}` : " · somente consulta"}</p>}
              </div>
              <Button variant={v.status === "pendente" ? "default" : "outline"} onClick={() => setEdit({ ...v, perm: v.status === "pendente" ? PADRAO : { leitura: v.leitura, operar: v.operar } })}>
                {v.status === "pendente" ? "Aprovar" : "Permissões"}</Button>
              <Button variant="ghost" onClick={() => revogar(v)}><Trash2 className="mr-1 h-4 w-4" />{v.status === "pendente" ? "Recusar" : "Encerrar acesso"}</Button>
            </li>
          ))}
        </ul>
      )}
      <p className="text-xs text-muted-foreground">O contador entra com o login do escritório, sem saber a sua senha. Tudo o que ele fizer fica registrado no histórico. Certificado digital, usuários, pagamentos e backup nunca ficam disponíveis para o contador.</p>

      <Dialog open={!!novo} onOpenChange={(o) => !o && setNovo(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Liberar escritório contábil</DialogTitle><DialogDescription>O código está no painel do escritório (Escritório contábil → Código do escritório).</DialogDescription></DialogHeader>
          {novo && <div className="space-y-3">
            <Field label="Código do escritório"><Input value={novo.codigo} onChange={(e) => setNovo({ ...novo, codigo: e.target.value.replace(/\D/g, "").slice(0, 8) })} inputMode="numeric" data-testid="contador-codigo-input" /></Field>
            {d && <Permissoes areas={d.areas} operaveis={d.operaveis} value={novo.perm} onChange={(perm) => setNovo({ ...novo, perm })} />}
            {d && <Termo texto={d.termo} value={novo.aceite} onChange={(aceite) => setNovo({ ...novo, aceite })} />}
          </div>}
          <DialogFooter><Button onClick={liberar} disabled={(novo?.codigo || "").length !== 8 || !novo?.aceite} data-testid="contador-liberar-confirmar">Liberar</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{edit?.escritorio?.nome}</DialogTitle><DialogDescription>Consultar = só ver e baixar. Operar = também emitir notas, lançar folha ou ajustar ponto.</DialogDescription></DialogHeader>
          {edit && d && <Permissoes areas={d.areas} operaveis={d.operaveis} value={edit.perm} onChange={(perm) => setEdit({ ...edit, perm })} />}
          {edit?.status === "pendente" && d && <Termo texto={d.termo} value={!!edit.aceite} onChange={(aceite) => setEdit({ ...edit, aceite })} />}
          <DialogFooter><Button onClick={salvar} disabled={edit?.status === "pendente" && !edit?.aceite}>{edit?.status === "pendente" ? "Aprovar acesso" : "Salvar"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!hist} onOpenChange={(o) => !o && setHist(null)}>
        <DialogContent className="max-w-2xl max-h-[85dvh] overflow-y-auto">
          <DialogHeader><DialogTitle>O que o contador fez</DialogTitle></DialogHeader>
          <ul className="divide-y text-sm">
            {(hist || []).map((h) => <li key={h.id} className="py-2"><b>{h.action}</b> <span className="text-muted-foreground">· {h.actor_name} · {fmtDateTime(h.created_at)}</span>{h.details && <span className="block text-xs text-muted-foreground">{h.details}</span>}</li>)}
            {hist?.length === 0 && <li className="py-2 text-muted-foreground">Nada registrado ainda.</li>}
          </ul>
        </DialogContent>
      </Dialog>
    </div>
  );
}
